import {
  Box,
  Button,
  Divider,
  HStack,
  Heading,
  Stack,
  Table,
  Tbody,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
} from '@chakra-ui/react';
import { isPast, parseISO } from 'date-fns';
import { useListCoaches, useListLocations, useListVisits, useClientWaitlist } from 'hooks/useApiQueries';
import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ClientVisit } from 'types/client';
import VisitsHistoryItem from './VisitsHistoryItem';
import WaitlistHistoryItem from './WaitlistHistoryItem';

// Type pour les entrées de waitlist
interface WaitlistEntry {
  Id: number;
  ClassId: number;
  ClassDate: string;
  StartTime: string;
  StartDateTime: string;
  ClassSchedule?: {
    StartTime: string;
    ClassDescription?: {
      Name?: string;
    };
    Staff?: {
      Id: number;
    };
    Location?: {
      Id: number;
    };
  };
}

export function VisitsHistory(): JSX.Element {
  const { data: visits } = useListVisits() || {};
  const { data: waitlistEntries } = useClientWaitlist() || {};

  const { t } = useTranslation();
  const navigate = useNavigate();

  const { pastVisits, futureVisits } = useMemo(() => {
    if (!visits) return { pastVisits: [], futureVisits: [] };

    const past: ClientVisit[] = [];
    const future: ClientVisit[] = [];

    visits.forEach((visit: ClientVisit) => {
      if (isPast(parseISO(visit.StartDateTime))) {
        past.push(visit);
      } else {
        future.push(visit);
      }
    });

    return {
      pastVisits: past.sort((a, b) => new Date(b.StartDateTime).getTime() - new Date(a.StartDateTime).getTime()),
      futureVisits: future.sort((a, b) => new Date(a.StartDateTime).getTime() - new Date(b.StartDateTime).getTime()),
    };
  }, [visits]);

  useEffect(() => {
    if (
      visits &&
      pastVisits.length === 0 &&
      futureVisits.length === 0 &&
      (!waitlistEntries || waitlistEntries.length === 0)
    ) {
      navigate('/account');
    }
  }, [visits, pastVisits, futureVisits, waitlistEntries, navigate]);

  return (
    <Stack>
      {(futureVisits.length > 0 || (waitlistEntries && waitlistEntries.length > 0)) && (
        <Box my={4} className="futureVisitsHistory">
          <HStack width="100%" justifyContent="space-between" margin="1em 0 2em">
            <Heading className="accountMainTitle" size="md" textTransform="uppercase">
              {t('Cours à venir')}
            </Heading>
            <Button variant="contained" onClick={() => navigate(-1)}>
              Retour
            </Button>
          </HStack>

          <Visits visits={futureVisits} />

          {waitlistEntries && waitlistEntries.length > 0 && (
            <>
              <Divider my={4} />
              <Heading size="sm" mb={4} textTransform="uppercase">
                {t("Listes d'attente")}
              </Heading>
              <WaitlistEntries entries={waitlistEntries as unknown as WaitlistEntry[]} />
            </>
          )}
        </Box>
      )}

      <Divider />

      {pastVisits.length > 0 && (
        <Box my={4} className="pastVisitsHistory">
          <HStack width="100%" justifyContent="space-between" margin="1em 0 2em">
            <Heading className="accountMainTitle" size="md" textTransform="uppercase">
              {t('Cours passés')}
            </Heading>
            <Button variant="contained" onClick={() => navigate(-1)}>
              Retour
            </Button>
          </HStack>

          <Visits visits={pastVisits} />
        </Box>
      )}
    </Stack>
  );
}

function Visits({ visits }: { visits: ClientVisit[] }) {
  const { t } = useTranslation();
  const isMobile = useBreakpointValue({ base: true, md: false }) || false;

  const visitsWithoutCrossRegionVisit = visits?.filter((visit) => visit.VisitType !== -1);

  const { data: coaches } = useListCoaches();
  const { data: locations } = useListLocations();
  const getCoachById = (visit: ClientVisit) =>
    coaches?.find((coach) => coach.Id === visit.StaffId && visit.SiteId === coach.SiteId);
  const getLocationById = (visit: ClientVisit) =>
    locations?.find((location) => location.Id === visit.LocationId && location.SiteID === visit.SiteId);

  return (
    <Table variant="simple">
      <Thead>
        <Tr>
          <Th color="black" fontSize="16px">
            {t('Nom')}
          </Th>
          <Th color="black" fontSize="16px">
            {t('Début')}
          </Th>
          <Th color="black" fontSize="16px">
            {t('Studio')}
          </Th>
          {!isMobile && (
            <>
              <Th color="black" fontSize="16px">
                {t('Spot')}
              </Th>
              <Th color="black" fontSize="16px">
                {t('Coach')}
              </Th>
              <Th color="black" fontSize="16px">
                {t('Action / Statut')}
              </Th>
            </>
          )}
        </Tr>
      </Thead>
      <Tbody>
        {visitsWithoutCrossRegionVisit.map((visit, index) => {
          const coach = getCoachById(visit);
          const location = getLocationById(visit);

          const coachName = coach ? coach?.FirstName?.toString() : '';
          const locationName = location ? location?.Name?.toString() : '';

          return (
            <VisitsHistoryItem
              isMobile={isMobile}
              key={visit.Id}
              index={index}
              visit={visit}
              coachName={coachName}
              locationName={locationName}
            />
          );
        })}
      </Tbody>
    </Table>
  );
}

function WaitlistEntries({ entries }: { entries: WaitlistEntry[] }) {
  const { t } = useTranslation();
  const isMobile = useBreakpointValue({ base: true, md: false }) || false;

  const { data: coaches } = useListCoaches();
  const { data: locations } = useListLocations();

  return (
    <Table variant="simple">
      <Thead>
        <Tr>
          <Th color="black" fontSize="16px">
            {t('Nom')}
          </Th>
          <Th color="black" fontSize="16px">
            {t('Début')}
          </Th>
          <Th color="black" fontSize="16px">
            {t('Studio')}
          </Th>
          {!isMobile && (
            <>
              <Th color="black" fontSize="16px">
                {t('Coach')}
              </Th>
              <Th color="black" fontSize="16px">
                {t('Action')}
              </Th>
            </>
          )}
        </Tr>
      </Thead>
      <Tbody>
        {entries.map((entry, index) => {
          const coach = coaches?.find((c) => c.Id === entry.ClassSchedule?.Staff?.Id);
          const location = locations?.find((l) => l.Id === entry.ClassSchedule?.Location?.Id);

          const coachName = coach ? coach?.FirstName?.toString() : '';
          const locationName = location ? location?.Name?.toString() : '';

          return (
            <WaitlistHistoryItem
              isMobile={isMobile}
              key={entry.Id}
              index={index}
              entry={entry}
              coachName={coachName}
              locationName={locationName}
            />
          );
        })}
      </Tbody>
    </Table>
  );
}
